import type { ReactNode } from 'react';
import { flexRender, type Row, type Table } from '@tanstack/react-table';
import {
  Table as UITable,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

/**
 * Canonical TanStack header/body `flexRender` skeleton, extracted from six
 * copy-pasted sites (deep-code-analysis §3). Accepts an ALREADY-BUILT table
 * instance so it works for both Refine's `useTable(...).reactTable` (Family A —
 * no loading/empty) and a raw `useReactTable` (Family B — with loading + empty
 * rows). When neither `isLoading` nor `emptyMessage` is passed it behaves
 * exactly like the old Family A markup. Dependency-light (no hub-client).
 */
interface DataTableProps<T> {
  table: Table<T>;
  isLoading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  colSpan?: number;
  /** Optional filter bar rendered inside the card, above the header row. */
  toolbar?: ReactNode;
  /**
   * Optional phone layout: below `md` each row renders as this card in a
   * list and the table is hidden; from `md` up the table shows as usual.
   */
  renderCard?: (row: Row<T>) => ReactNode;
}

/** With a phone card list the table shows from `md` up only; otherwise it renders unwrapped. */
function DesktopOnly({ when, children }: { when: boolean; children: ReactNode }) {
  return when ? <div className="hidden md:block">{children}</div> : <>{children}</>;
}

export function DataTable<T>({
  table,
  isLoading,
  loadingMessage = 'Loading…',
  emptyMessage,
  colSpan,
  toolbar,
  renderCard,
}: DataTableProps<T>) {
  const rows = table.getRowModel().rows;
  const resolvedColSpan = colSpan ?? table.getAllLeafColumns().length;
  // TanStack answers `getCanNextPage()` from a default 10-row page size even
  // when the table has no pagination row model, so a table that never asked to
  // be paginated would get page buttons that move state nobody reads. Family B
  // pages (raw `useReactTable`) either paginate server-side themselves or not
  // at all — only offer the pager to a table that opted in.
  const isPaginated =
    table.options.manualPagination === true || table.options.getPaginationRowModel !== undefined;
  const canPrevious = isPaginated && table.getCanPreviousPage();
  const canNext = isPaginated && table.getCanNextPage();
  const showFooter = !isLoading && (rows.length > 0 || canPrevious);

  return (
    <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
      {toolbar && <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2.5">{toolbar}</div>}
      {renderCard && (
        <ul className="divide-y md:hidden">
          {isLoading ? (
            <li className="px-4 py-3 text-center text-sm text-muted-foreground">{loadingMessage}</li>
          ) : emptyMessage !== undefined && rows.length === 0 ? (
            <li className="px-4 py-3 text-center text-sm text-muted-foreground">{emptyMessage}</li>
          ) : (
            rows.map((row) => <li key={row.id}>{renderCard(row)}</li>)
          )}
        </ul>
      )}
      <DesktopOnly when={renderCard !== undefined}>
      <UITable>
      <TableHeader>
        {table.getHeaderGroups().map((hg) => (
          <TableRow key={hg.id}>
            {hg.headers.map((h) => (
              <TableHead key={h.id}>
                {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoading ? (
          <TableRow>
            <TableCell colSpan={resolvedColSpan} className="text-center text-muted-foreground">
              {loadingMessage}
            </TableCell>
          </TableRow>
        ) : emptyMessage !== undefined && rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={resolvedColSpan} className="text-center text-muted-foreground">
              {emptyMessage}
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => (
            <TableRow key={row.id} className="group">
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
      </UITable>
      </DesktopOnly>
      {showFooter && (
        <div className="flex items-center justify-between border-t px-4 py-2.5 text-xs text-muted-foreground">
          <span>
            {rows.length} {rows.length === 1 ? 'row' : 'rows'}
          </span>
          {(canPrevious || canNext) && (
            <div className="flex gap-2">
              <button
                type="button"
                className="cursor-pointer rounded-[var(--radius-ctl)] border bg-card px-3 py-1 font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-default disabled:opacity-50"
                disabled={!canPrevious}
                onClick={() => table.previousPage()}
              >
                Previous
              </button>
              <button
                type="button"
                className="cursor-pointer rounded-[var(--radius-ctl)] border bg-card px-3 py-1 font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-default disabled:opacity-50"
                disabled={!canNext}
                onClick={() => table.nextPage()}
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
